import React from 'react'
import { axiosGet } from '../../common/js/axios-utility'

import { PurchaseContext } from '../contexts/PurchaseContext'
import { errorToast } from '../../common/js/toast-utility';
import SubscriptionSelect from './SubscriptionSelect';
import SubscriptionStoreSelect from './SubscriptionStoreSelect';
import SerialSelect from './SerialSelect';
import AddToCartButton from './AddToCartButton';
import CurrencySelect from '../../common/components/CurrencySelect';

import Qty from "./Qty";
import QtyStore from "./QtyStore";
import { type ProductFamily, type WebProduct } from '../../common/types/webshop.types';
import { type SelectOption } from '../../common/types/common.types';
import { type SerialOption } from '../../common/types/webshop.types';
import { fetchDefaultCurrency, formatCurrency } from '../../common/js/currency-utility';
import { sendViewItemEvent } from '../../common/js/analytics-utility';
import { useTranslation } from 'react-i18next';

type ProductWrapper =  {
    id: string,
    name: string,
    variants: VariantMap,
    hosts: SelectOption[],
    types: SelectOption[],
    subfamilies: ProductFamily[],
    html: {__html: string},
    bannerUrl?: string
}

type VariantMap = {
    [host: string]: {
        [type: string]: WebProduct
    }
}

type ProductSelectState = {
    currencyIso: string,
    product: ProductWrapper,
    subproduct: ProductWrapper,
    selections: Record<string, string>,
    subscriptionVariants: Record<string, WebProduct>,
    renewalVariants: Record<string, WebProduct>,
    serial: SerialOption,
    defaultSerial: string,
    quantity: number,
    submitting: boolean 
}

type ProductSelectAction = 
    | { type: "RESET" }
    | { type: "RESET_PRODUCT" }
    | { type: "SET_CURRENCY", payload: ProductSelectState['currencyIso']}
    | { type: "SET_PRODUCT", payload: ProductSelectState['product']}
    | { type: "SET_SELECTIONS", payload: ProductSelectState['selections']}
    | { type: "SET_SUBPRODUCT", payload: ProductSelectState['subproduct']}
    | { type: "SET_SERIAL", payload: ProductSelectState['serial']}
    | { type: "SET_DEFAULT_SERIAL", payload: ProductSelectState['defaultSerial']}
    | { type: "SET_RENEWAL_OPTIONS", payload: ProductSelectState['renewalVariants']}
    | { type: "SET_SUBSCRIPTION_OPTIONS", payload: ProductSelectState['subscriptionVariants']}
    | { type: "quantity", payload: number}
    | { type: "SET_CURRENCY", payload: string}
    | { type: "SET_SUBMITTING", payload: ProductSelectState['submitting']}

const initialProductState: ProductSelectState = {
    currencyIso: 'USD',
    product: {id: '', name: '', variants: {}, hosts: [], types: [], subfamilies: [], html: {__html: ''}},
    subproduct: null,
    selections: {host: null, type: null, subscriptionType: ''},
    subscriptionVariants: {'monthly' : null, 'annual' : null},
    renewalVariants: {'renewal' : null, 'reinstatement' : null},
    serial: null,
    defaultSerial: null,
    quantity: 1,
    submitting: false,
}

const reducer = (state: ProductSelectState, action: ProductSelectAction) => {
    switch(action.type){
        case 'RESET':
            return initialProductState;
        case 'RESET_PRODUCT':
            return {...initialProductState};
        case 'SET_CURRENCY':
            return {...state, currencyIso: action.payload};
        case 'SET_PRODUCT':
            return {...initialProductState, product: action.payload};
        case 'SET_SUBPRODUCT':
            return {...state, subproduct: action.payload, selections: {host: null, type: null, subscriptionType: ''}};    
        case 'SET_SELECTIONS':
            return {...state, selections: action.payload};
        case 'SET_SERIAL':
            return {...state, serial: action.payload};
        case 'SET_DEFAULT_SERIAL':
            return {...state, defaultSerial: action.payload};
        case 'SET_RENEWAL_OPTIONS':
            return {...state, renewalVariants: action.payload};
        case 'SET_SUBSCRIPTION_OPTIONS':
            return {...state, subscriptionVariants: action.payload};
        case 'quantity':
            return {...state, quantity: action.payload};
        case 'SET_SUBMITTING':
            return {...state, submitting: action.payload};
    }
}

type Props = {
    onAddToCart: Function,
    addToCartLabel?: string
}

export default function ProductSelect(props: Props) {    
    const {onAddToCart, addToCartLabel} = props;

    //STATE HOOKS
    //#state-hooks
    const [purchaseState, setPurchaseState] = React.useContext(PurchaseContext);
    const [productState, dispatch] = React.useReducer(reducer, initialProductState);
    //TRANSLATION HOOK
    //#translation-hook
    const { t } = useTranslation(['checkout', 'common']);
    
    //PRE-RENDER CALCULATIONS
    const purchaseForm = React.useRef(null);
    const {quantity, selections, subscriptionVariants, defaultSerial, currencyIso} = productState;
    const parentProduct = productState.product;
    const product = productState.subproduct || parentProduct;
    
    let selectedVariant: WebProduct = fetchVariant(product, selections);
    const calculatePrices = selectedVariant != null && quantity > 0;

    const price = calculatePrices ? formatCurrency(selectedVariant.prices[currencyIso], currencyIso) : '$0.00';
    const compareAt = calculatePrices ? formatCurrency(selectedVariant.listPrices[currencyIso], currencyIso) : '$0.00';
    const isRenewalVariant = renewalVariantTitles.includes(productState.selections.type);
    const requiresManualSerialEntry = calculatePrices ? serialEntryTitles.includes(selectedVariant.typeLabel) : false;
    const isSalePrice = (selectedVariant != null && price != compareAt);
    
    //EFFECT HOOKS
    //#effect-hooks
    React.useEffect(() => {
        setPurchaseState({...purchaseState, loading: true});
        if (!purchaseState.family){
            dispatch({type: 'RESET'});
            return;
        }

        const doInit = async () => {
            try{
                const {defaults, family} = purchaseState;
                const defaultProductName = family;
                const parentProduct = await fetchProduct(defaultProductName);
                const defaultCurrency = await fetchDefaultCurrency();
                const defaultSubproduct = getDefaultSubproduct(parentProduct, defaults.subfamily); 
                const product = defaultSubproduct || parentProduct;
                const defaultSelections = {
                    ...productState.selections, 
                    subproduct: defaults.subfamily,
                    host: defaults.host, 
                    type: defaults.type, 
                    subscriptionType: defaults.subscriptionType,
                };
                defaultSelections.host = defaultHost(defaults.host, product);
                defaultSelections.type = defaultType(defaults.type, product);
                dispatch({type: 'SET_PRODUCT', payload: parentProduct});
                dispatch({type: 'SET_SUBPRODUCT', payload: defaultSubproduct});
                dispatch({type: 'SET_SELECTIONS', payload: defaultSelections});
                dispatch({type: 'SET_CURRENCY', payload: defaultCurrency});
                if (defaults.serial){
                    dispatch({type: 'SET_DEFAULT_SERIAL', payload: defaults.serial});
                }
                setPurchaseState({...purchaseState, productHtml: product.html, bannerUrl: product.bannerUrl, loading: false});

                setDeepLink(parentProduct, defaultSubproduct, defaultSelections);
            }
            catch(err){
                console.error(err);
                errorToast('There was an error loading this product. Please try again.')
            }
        }
        doInit();
    }, [purchaseState.family, purchaseState.defaults])

    React.useEffect(() => {
        const form = purchaseForm.current;
        productState.selections.host
        if (form.host) form.host.value = productState.selections.host || '';
        if (form.type) form.type.value = productState.selections.type || '';
        if (productState.selections.type == 'Subscription'){
            const prod = productState.subproduct || productState.product;
            const subTypes = getSubscriptionVariants(prod, productState.selections);
            dispatch({type: 'SET_SUBSCRIPTION_OPTIONS', payload: subTypes});
        }
        else if (productState.selections.type == 'U&S Renewal'){
            const renewalTypes = getRenewalVariants(productState.product, productState.selections);
            dispatch({type: 'SET_RENEWAL_OPTIONS', payload: renewalTypes});
        }
        setDeepLink(productState.product, productState.subproduct, productState.selections);
        sendGaEvent(productState.product, productState.selections, quantity);
    }, [productState.selections]);

    React.useEffect(() => {
        const form = purchaseForm.current;
        if (form.product){
            form.product.value = productState.subproduct.name;
        }
        setDeepLink(productState.product, productState.subproduct, productState.selections);
    }, [productState.subproduct])

    const setDeepLink = (product: ProductWrapper, subproduct: ProductWrapper, selections: Record<string, string>) => {
        if (!selectedVariant || !product.name || !purchaseState.useDeeplinks) return;
        const host = selectedVariant.hostLabel || selectedVariant.host;
        const type = selections.subscriptionType ? selections.subscriptionType : selections.type;
    
        let url = window.location.protocol + "//" + window.location.host + window.location.pathname + '?';
        url += `product=${slugify(product.name)}`
        if (subproduct){
            url += '&subproduct=' + slugify(subproduct.name)
        }
        if (host){
            url += '&host=' + slugify(host)
        }
        if (type){
            url += '&purchase-options=' + slugify(type)
        }
        window.history.pushState({}, '', url);
    }

    //FORM CHANGE HANDLERS
    //#form-change-handlers

    const handleProductChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPurchaseState({...purchaseState, loading: true});
        const productName = event.target.value;
        const subproduct = productState.product.subfamilies.find(p => {
            return p.name == productName;
        })
        const subproductNew = compileProduct(subproduct);
        dispatch({type: 'SET_SUBPRODUCT', payload: subproductNew});
        setPurchaseState({...purchaseState, productHtml: subproductNew.html, bannerUrl: subproductNew.bannerUrl, loading: false});
    }

    const handleOptionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectionsNew: Record<string, string> = {...productState.selections, [event.target.name] : event.target.value};
        if (selectionsNew.type != 'Subscription') {
            selectionsNew.subscriptionType = null;
        } else if (selections.type != 'Subscription') {
            selectionsNew.subscriptionType = 'New Annual Subscription';
        }

        if (selectionsNew.type != 'U&S Renewal') {
            dispatch({type : 'SET_SERIAL', payload: initialProductState.serial});
        }
        dispatch({type : 'SET_SELECTIONS', payload: selectionsNew});
    }

    const handleLicenseTypeChange = (variantName: string) => {
        const selectionsNew: Record<string, string> = {...productState.selections, ['type'] : variantName};

        if (selectionsNew.type != 'Subscription') {
            selectionsNew.subscriptionType = null;
        } else if (selections.type != 'Subscription') {
            selectionsNew.subscriptionType = 'New Annual Subscription';
        }

        if (selectionsNew.type != 'U&S Renewal'){
            dispatch({type : 'SET_SERIAL', payload: initialProductState.serial});
        }

        dispatch({type : 'SET_SELECTIONS', payload: selectionsNew});
    }

    const handleSubscriptionChange = async (event: React.MouseEvent<HTMLDivElement>) => {
        const subType = event.currentTarget.dataset.name;
        const selectionsNew = {...productState.selections, subscriptionType: subType}
        dispatch({type: 'SET_SELECTIONS', payload: selectionsNew});
    }

    const handleSerialChange = async (event: React.ChangeEvent<HTMLSelectElement|HTMLInputElement>) => {
        const serialString = event.target.value;
        let serial;

        if (!requiresManualSerialEntry){
            serial = purchaseState.serials[serialString];
        }
        else{
            serial = {serial : serialString, quantity: 1};
        }
        dispatch({type: 'SET_SERIAL', payload: serial});
        
        if (!requiresManualSerialEntry) updateQuantity(serial.qty);
    }

    const updateQuantity = (qty: number) => {
        const qtyInput = document.getElementById('purchase-qty');
        if (qtyInput instanceof HTMLInputElement){
            qtyInput.value = qty.toString();
        }
        dispatch({type: 'quantity', payload: qty})
    }

    const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        const qtyInput = document.getElementById('purchase-qty');
        const data = event.target.dataset;

        let val = productState.quantity;
        if (data.qty == 'add') {
            val++;
        } else if (data.qty == 'subtract'){
            val = val > 1 ? val - 1 : val;
        } else {
            val = parseInt(event.target.value);
        }
        dispatch({type: 'quantity', payload: val});
        if (qtyInput instanceof HTMLInputElement){
            qtyInput.value = val.toString();
        }
    }

    const handleCurrencyChange = (newIso: string) => {
        dispatch({type: 'SET_CURRENCY', payload: newIso});
    }

    //DATA CALCULATION METHODS
    //#calculation-methods
    const productSelected = () => {
        return (
            selectedVariant != null &&
            (!isRenewalVariant || productState.serial)
        );
    }

    const defaultHost = (defaultHostName: string, product: ProductWrapper) => {
        const hostName = defaultHostName || '';
        const hostSlug = slugify(hostName);
        const defaultHost = product.hosts.find(h => {
            return slugify(h.name) == hostSlug;
        })
        if (!defaultHost){
            return product.hosts[0].name;
        }else{
            return defaultHost.name;
        }
    }

    const defaultType = (defaultTypeName: string, product: ProductWrapper) => {
        const typeName = defaultTypeName || '';
        const defaultType = product.types.find(t => {
            return t.name.toLowerCase() == typeName.toLowerCase();
        })
        if (!defaultType){
            return product.types[0].name;
        }
        else{
            return defaultType.name;
        }
    }

    //Populate Select Options
    const productOptions: React.JSX.Element[] = [];
    parentProduct.subfamilies.forEach(subfam => {
        productOptions.push(
            <option key={subfam['name']} value={subfam['name']}>{subfam['name']}</option>
        )
    })

    const hostOptions: React.JSX.Element[] = [];
    product.hosts.forEach(host => {
        hostOptions.push(
            <option key={host.name} value={host.name}>{host.label}</option>
        )
    })

    const variantOptionsDiv = [];
    product.types.forEach(variant => {
        if(variant.name !== 'U&S Renewal') {
            variantOptionsDiv.push(
                <div className="plan-item">
                    <div key={variant.name}
                         className={`subscription-option ${selections.type === variant.name ? 'selected' : ''}`}
                         onClick={() => handleLicenseTypeChange(variant.name)}>
                        <span className="title">{variant.label}</span>
                    </div>
                </div>
            )
        }
    })

    const variantOptions: React.JSX.Element[] = [];
    product.types.forEach(variant => {
        variantOptions.push(
            <option key={variant.name} value={variant.name}>{variant.label}</option>
        )
    })

    const showDetails = productSelected()

    //RENDER
    //#render
    return (
        <>
            <form ref={purchaseForm} id="purchase-form">
            {productOptions.length > 0 &&(
            <div className='form-group' hidden={!product.id}>
                <label>{t('license.product', {ns: 'common'})}</label>
                <select className='form-control' name="product" onChange={handleProductChange}>
                    {productOptions}
                </select>
            </div>
            )}
            {hostOptions.length > 0 && (
                <div className='form-group'>
                    <label>{t('license.host', {ns: 'common'})}</label>
                    <select className='form-control' name="host" onChange={handleOptionChange}>
                        <option value="">--- {t('license.selectHost', {ns: 'common'})} ---</option>
                        {hostOptions}
                    </select>
                </div>
            )}
            {variantOptions.length > 0 && (
                <div className='form-group'>
                    <label>{t('license.licType', {ns: 'common'})}</label>
                    <select className='form-control' disabled={selections.host ? false : true} name="type" onChange={handleOptionChange}>
                        <option value="">--- {t('form.selectOption', {ns: 'common'})} ---</option>
                        {variantOptions}
                    </select>
                </div>
            )}

                {selections.type == 'Subscription' && selections.type && selections.host && purchaseState.store && (
                    <div>
                        <div>
                            <div className='plans-pricing-header'>
                                <label>{t('purchase.plans')}</label>
                                <CurrencySelect selectedIso={currencyIso} onChange={handleCurrencyChange} />
                            </div>
                            <SubscriptionSelect
                                onSelect={handleSubscriptionChange}
                                selected={selections.subscriptionType}
                                isSalePrice={isSalePrice}
                                monthlyVariant={subscriptionVariants.monthly}
                                annualVariant={subscriptionVariants.annual}
                                currencyIso={currencyIso}
                            />
                        </div>
                    </div>
                )}

                {selections.type != 'Subscription' 
                 && selections.type != 'Perpetual License'
                 && selections.type 
                 && selections.host 
                 && purchaseState.store && (
                    <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                        <span style={{fontWeight: '600', fontSize: '32px'}}>{formatCurrency(selectedVariant.price, currencyIso)}</span>
                    </div>
                )}

                {selections.type == 'Subscription' && !purchaseState.store && (
                    <div className="row">
                        <div className="col-12">
                            <SubscriptionStoreSelect
                                onSelect={handleSubscriptionChange}
                                selected={selections.subscriptionType}
                                monthlyVariant={subscriptionVariants.monthly}
                                annualVariant={subscriptionVariants.annual}
                                currencyIso={currencyIso}
                            />
                        </div>
                    </div>
                )}

                {selections.type === 'New Permanent License' && purchaseState.store && (
                    <div>
                        <div>
                            <div className='plans-pricing-header'>
                                <label>{t('purchase.plans')}</label>
                                <CurrencySelect selectedIso={currencyIso} onChange={handleCurrencyChange} />
                            </div>
                            <div className="plan-pricing">
                                <div className="plan-item">
                                    <div className="subscription-option selected">
                                        <span className="title">Perpetual License</span>
                                        <div className='price-over'>
                                            {!isSalePrice && (
                                                <>
                                                <div className='price-total'><span>{price}</span></div>
                                                </>
                                            )}
                                            {isSalePrice && (
                                                <>
                                                <div className='price-total'><span className='price-total-discount'>{price}</span></div>
                                                <div className='annual-list-price'>{compareAt}</div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {isRenewalVariant && (
                    <div className={!purchaseState.store ? 'row' : ''}>
                        <div className={!purchaseState.store ? 'col-12' : ''}>
                            <SerialSelect
                                onChange={handleSerialChange}
                                host={selectedVariant ? selectedVariant.host : null}
                                product={product.name}
                                defaultSerial={defaultSerial}
                            />
                        </div>
                    </div>
                )}

                {requiresManualSerialEntry && (
                    <div className={!purchaseState.store ? 'row' : ''}>
                        <div className={!purchaseState.store ? 'col-12' : ''}>
                            <div className='form-group' style={{margin: 0}}>
                                <label>Serial</label>
                                <input className='form-control' type="text" name="serial" onChange={handleSerialChange}/>
                            </div>
                        </div>
                    </div>
                )}

                {!purchaseState.store && (
                    <div className={showDetails ? '' : 'hidden'}>
                        <QtyStore onSelect={handleQuantityChange}
                                  selected={selections.subscriptionType}
                                  isRenewalVariant={isRenewalVariant}
                                  isSalePrice={isSalePrice}
                                  price={price}
                                  compareAt={compareAt}
                                  selectedVariant={selectedVariant}
                        />
                        <AddToCartButton
                            variant={selectedVariant}
                            quantity={productState.quantity}
                            serial={productState.serial}
                            renewalVariants={productState.renewalVariants}
                            onComplete={onAddToCart}
                            label={addToCartLabel}
                            currencyIso={currencyIso}
                        />
                    </div>
                )}

                {purchaseState.store && (
                    <div className={selections.type && selections.host ? 'qty-block' : 'qty-block disabled'}>
                        <Qty onSelect={handleQuantityChange}
                             selected={selections.subscriptionType}
                             isRenewalVariant={isRenewalVariant}
                             quantity={productState.quantity}
                        />
                        <AddToCartButton
                            variant={selectedVariant}
                            quantity={productState.quantity}
                            serial={productState.serial}
                            renewalVariants={productState.renewalVariants}
                            onComplete={onAddToCart}
                            label={addToCartLabel}
                            currencyIso={currencyIso}
                        />
                    </div>
                )}
            </form>
        </>
    )
}
const renewalVariantTitles = ['U&S Renewal', 'Legacy U&S Renewal'];
const serialEntryTitles = ['Upgrade from particleIllusion 3', 'Upgrade From Previous Version', 'Upgrade from legacy SynthEyes license'];

const fetchProduct = async (productName: string) => {
    const res = await axiosGet(`/shop/product?name=${productName}`);
    const fetchedProduct = res.data;
    const productNew = compileProduct(fetchedProduct);

    return productNew;
}

const compileProduct = (fetchedProduct: ProductFamily) => {
    const variantData = compileVariants(fetchedProduct);
    const productNew: ProductWrapper = {
        id: fetchedProduct.id,
        name: fetchedProduct.name,
        html: {__html : fetchedProduct.htmlDescription},
        bannerUrl: fetchedProduct.bannerUrl,
        subfamilies: fetchedProduct.subfamilies || [],
        ...variantData
    }
    return productNew;
}

const fetchVariant = (product: ProductWrapper, selections: Record<string, string>) => {
    let host = selections.host || null;
    const purchaseOptions = product.variants[host];
    const variantNew = {};
    if (!purchaseOptions){
        return null;
    } 
    let productType = selections.subscriptionType ? selections.subscriptionType : selections.type;
    if (!productType) productType = null;
    return purchaseOptions[productType] || null;
}

const getDefaultSubproduct = (product: ProductWrapper, defaultName: string) => {
    const def = defaultName || '';
    if (product.subfamilies.length < 1){
        return null;
    }
    let subproduct = product.subfamilies.find(sp => {
        return slugify(sp.name) == slugify(defaultName);
    })
    if (!subproduct){
        subproduct = product.subfamilies[0];
    }
    const compiledSubproduct = compileProduct(subproduct);
    return compiledSubproduct;
}

const getSubscriptionVariants =  (product: ProductWrapper, selections: Record<string, string>) => {
    const host = selections.host || null;
    const purchaseOptions = product.variants[host];
    const subOptionsNew: Record<string, WebProduct> = {};
    if (!purchaseOptions){
        return subOptionsNew
    }
    subOptionsNew.monthly = purchaseOptions['New Monthly Subscription'];
    subOptionsNew.annual = purchaseOptions['New Annual Subscription'];
    return subOptionsNew;
}

const getRenewalVariants =  (product: ProductWrapper, selections: Record<string, string>) => {
    const purchaseOptions = product.variants[selections.host];
    const renewalOptionsNew: Record<string, WebProduct> = {};
    if (!purchaseOptions){
        return renewalOptionsNew
    }
    renewalOptionsNew.renewal = purchaseOptions['U&S Renewal'];
    renewalOptionsNew.reinstatement = purchaseOptions['Legacy U&S Renewal'];
    return renewalOptionsNew;
}

const compileVariants = (product: ProductFamily) => {
    const result: any = {variants : {}, hosts: [], types: []};
    const variants = result.variants;
    const hiddenTypes = ['New Monthly Subscription', 'Legacy U&S Renewal'];
    const addedHosts: string[] = [];
    const addedTypes: string[] = [];

    product.products.forEach(vari => {
        const variNew = {...vari};
        
        const hostLabel = vari.hostLabel || vari.host;
        if (hostLabel != null && !addedHosts.includes(vari.host)){
            result.hosts.push({
                name: vari.host,
                label: hostLabel
            });
            addedHosts.push(vari.host);
        }

        let typeLabel = vari.typeLabel || vari.type;
        if (typeLabel != null && !hiddenTypes.includes(vari.type)){
            if (vari.type == 'New Annual Subscription'){
                typeLabel = 'Subscription';
            }
            if (typeLabel == 'Upgrade/Support Renewal'){typeLabel = 'Upgrade & Support Renewal'}
            if (!addedTypes.includes(typeLabel)){
                result.types.push({
                    name: typeLabel == 'Subscription' ? typeLabel : vari.type,
                    label: typeLabel
                })
            };
            addedTypes.push(typeLabel);
        }

        if (!variants[variNew.host]) variants[variNew.host] = {};
        variants[variNew.host][variNew.type] = variNew;
    })
    return result;
}

const sendGaEvent = (product: ProductWrapper, selections: Record<string, string>, quantity: number) => {
    try{
        const variant: WebProduct = fetchVariant(product, selections);
        sendViewItemEvent(variant, quantity);
    }
    catch(e){
        console.error(e);
    }
}

const slugify = (text: string) => {
    if (!text){
        return null;
    }
    return text.toString().toLowerCase()
	.replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/\//g, '-')            // Replace forward slashes with single -
	.replace(/[^\w\-]+/g, '')       // Remove all non-word chars
	.replace(/\-\-+/g, '-')         // Replace multiple - with single -
	.replace(/^-+/, '')             // Trim - from start of text
	.replace(/-+$/, '');            // Trim - from end of text
}